import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../config/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {
  GalleryVertical,
  ImagePlus,
  Images,
  Megaphone,
  School,
  SlidersHorizontal,
} from "lucide-react";

const CONTROL_CENTER_ITEMS = [
  {
    title: "Gallery Images",
    description: "Manage inauguration and school-life media.",
    path: "/manage-gallery-images",
    icon: Images,
    colorClasses: "bg-blue-100 text-blue-700",
  },
  {
    title: "Latest Updates",
    description: "Create and update notices for the home page.",
    path: "/manage-notices",
    icon: Megaphone,
    colorClasses: "bg-indigo-100 text-indigo-700",
  },
  {
    title: "Admission Image",
    description: "Update admission popup image and visibility.",
    path: "/manage-updates-popup",
    icon: ImagePlus,
    colorClasses: "bg-cyan-100 text-cyan-700",
  },
  {
    title: "Home Carousel",
    description: "Control top hero slides and autoplay settings.",
    path: "/manage-home-carousel",
    icon: SlidersHorizontal,
    colorClasses: "bg-sky-100 text-sky-700",
  },
];

const GALLERY_STEPS = [
  {
    key: "inauguration",
    title: "Inauguration Ceremony",
    description: "Manage inauguration ceremony photos/videos.",
    path: "/manage-gallery-images/inauguration",
    icon: GalleryVertical,
  },
  {
    key: "school-life",
    title: "Our School Life",
    description: "Manage school life photos/videos.",
    path: "/manage-gallery-images/school-life",
    icon: School,
  },
];

export default function ManageGalleryImages() {
  const navigate = useNavigate();
  const auth = getAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setIsAdmin(false);
        navigate("/");
        setLoading(false);
        return;
      }

      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const isAdminOrPrincipal =
        userSnap.exists() &&
        (userSnap.data()?.roles?.includes("admin") ||
          userSnap.data()?.roles?.includes("principal"));

      setIsAdmin(isAdminOrPrincipal);
      if (!isAdminOrPrincipal) {
        navigate("/");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-600">Access denied. Admin or Principal only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#12325b] via-[#1c4a80] to-[#12325b] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Media & Updates Manager
          </h1>
          <p className="text-blue-100">
            Unified control center for all home-page media and update functions.
          </p>
        </div>

        <div className="mb-8 rounded-2xl bg-white p-6 shadow-[0_16px_40px_rgba(18,50,91,0.25)]">
          <h2 className="text-xl font-bold text-[#12325b] mb-2">
            Control Center
          </h2>
          <p className="text-sm text-gray-600 mb-5">
            Use one place to manage all four related functionalities.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {CONTROL_CENTER_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.path === "/manage-gallery-images";
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className={`text-left rounded-xl border p-4 transition ${
                    isActive
                      ? "border-[#12325b] bg-blue-50"
                      : "border-slate-200 bg-white hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`rounded-lg p-2 ${item.colorClasses}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#12325b] text-sm">
                        {item.title}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 leading-5">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-[0_16px_40px_rgba(18,50,91,0.25)]">
          <h2 className="text-xl font-bold text-[#12325b] mb-2">
            Gallery Steps
          </h2>
          <p className="text-sm text-gray-600 mb-5">
            Open one section at a time to avoid confusion.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {GALLERY_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <button
                  key={step.key}
                  type="button"
                  onClick={() => navigate(step.path)}
                  className="text-left rounded-xl border border-slate-200 bg-white p-4 transition hover:border-blue-300 hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg p-2 bg-blue-100 text-blue-700">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                        Step {index + 1}
                      </p>
                      <p className="mt-2 font-semibold text-[#12325b]">
                        {step.title}
                      </p>
                      <p className="mt-1 text-xs text-gray-600 leading-5">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
